// src/components/desk/PasteBatchPanel.jsx — a paste's reviewable update batch, and the history of all of them
// (owner 2026-09-17, core 1241). Draws and collects decisions only; the rules live in src/lib/pasteBatch.js and
// the server decides what is written. Its own file so it can be rendered outside the login gate.
//
// Per batch: "Update N · date · source", counts, one row per field (current → proposed → decision), the
// supporting sentence and the current value's source on expand, "Apply selected changes", undo per decision
// and per batch — with a per-field question when a later update changed the field since.
import { useMemo, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { pill } from './primitives.jsx'
import { defaultDecisions, applyDecision, batchCounts, decisionsToSend } from '../../lib/pasteBatch.js'

const TONE = {
  add: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  conflict: 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  remove: 'border-rose-500/50 bg-rose-500/10 text-rose-700 dark:text-rose-300',
  ambiguous: 'border-sky-500/50 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  unchanged: 'border-border text-muted-foreground',
}
const IDLE = 'border-border text-muted-foreground hover:bg-muted'
const ON = 'bg-foreground text-background border-foreground'
const show = v => (v === null || v === undefined || v === '' ? '—' : v === true || v === 'true' ? t('desk_facts_yes') : v === false || v === 'false' ? t('desk_facts_no') : String(v))
const when = iso => {
  const ms = iso ? Date.parse(iso) : NaN
  return Number.isNaN(ms) ? '' : new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function Choice({ value, current, onPick, children }) {
  return <button type="button" aria-pressed={current === value} onClick={() => onPick(value)}
    className={pill('none', { door: true, extra: current === value ? ON : IDLE })}>{children}</button>
}

export function PasteBatch({ batch, number, sourceLabel, fieldLabel, onDecide, onUndo, busy = false, defaultOpen = true }) {
  const fields = useMemo(() => batch.fields || [], [batch.fields])
  const [open, setOpen] = useState(defaultOpen)
  const [decisions, setDecisions] = useState(() => defaultDecisions(fields))
  const [expanded, setExpanded] = useState(() => new Set())
  const [showUnchanged, setShowUnchanged] = useState(false)
  const [showText, setShowText] = useState(false)
  const [stale, setStale] = useState(null)          // [{field, reviewed, now}] — changed since review
  const [undoAsk, setUndoAsk] = useState(null)      // { fields, items:[{field, accepted_value, current_value, current_source}], choice:{} }
  const [msg, setMsg] = useState(null)
  const counts = batchCounts(fields)
  const toSend = decisionsToSend(fields, decisions)
  const accepted = fields.filter(f => f.decision === 'accept')
  const rows = useMemo(() => {
    const order = { conflict: 0, remove: 1, ambiguous: 2, add: 3, unchanged: 4 }
    return [...fields].sort((a, b) => (order[a.change_kind] ?? 9) - (order[b.change_kind] ?? 9))
  }, [fields])

  const pick = (field, d) => setDecisions(x => applyDecision(x, fields, field, d))
  const toggle = field => setExpanded(s => { const n = new Set(s); n.has(field) ? n.delete(field) : n.add(field); return n })

  // `sending` is remembered so "apply anyway" after a changed-since answer re-sends the SAME decisions
  // (a Reconsider sends one row, not the batch's open selection).
  const [lastSent, setLastSent] = useState(null)
  async function apply(ack = [], sending = ack.length && lastSent ? lastSent : toSend) {
    setMsg(null)
    setLastSent(sending)
    const r = await onDecide(batch.proposal_id, sending, ack)
    if (r?.stale) setStale(r.stale)
    else { setStale(null); if (r?.failures?.length) setMsg(tLog('desk_batch_failures', { list: r.failures.join(', ') })) }
  }
  async function undo(fieldsToUndo, confirm = [], skip = []) {
    setMsg(null)
    const r = await onUndo(batch.proposal_id, { fields: fieldsToUndo, confirm, skip })
    if (r?.needs_confirmation) setUndoAsk({ fields: fieldsToUndo, items: r.needs_confirmation, choice: {} })
    else {
      setUndoAsk(null)
      if (r?.not_undoable?.length) setMsg(tLog('desk_batch_not_undoable', { list: r.not_undoable.map(x => `${fieldLabel(x.field)} (${x.reason})`).join('; ') }))
    }
  }

  return (
    <div className="rounded-xl border border-border" data-paste-batch={batch.proposal_id}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="w-full flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-left">
        <span className="text-[13px] font-semibold text-foreground">
          {tLog('desk_batch_title', { n: number })} · <span className="font-normal text-muted-foreground">{when(batch.created_at)} · {sourceLabel(batch.source_kind)}</span>
        </span>
        <span className="flex flex-wrap items-center gap-1.5 text-[11px]">
          {counts.add > 0 && <span className={`rounded-full border px-1.5 leading-5 ${TONE.add}`}>{tLog('desk_batch_n_add', { n: counts.add })}</span>}
          {counts.conflict > 0 && <span className={`rounded-full border px-1.5 leading-5 ${TONE.conflict}`}>{tLog('desk_batch_n_conflict', { n: counts.conflict })}</span>}
          {counts.remove > 0 && <span className={`rounded-full border px-1.5 leading-5 ${TONE.remove}`}>{tLog('desk_batch_n_remove', { n: counts.remove })}</span>}
          {counts.ambiguous > 0 && <span className={`rounded-full border px-1.5 leading-5 ${TONE.ambiguous}`}>{tLog('desk_batch_n_ambiguous', { n: counts.ambiguous })}</span>}
          {counts.unchanged > 0 && <span className="text-muted-foreground">{tLog('desk_batch_n_unchanged', { n: counts.unchanged })}</span>}
          {batch.pending > 0 && <span className="rounded-full border border-amber-500/50 px-1.5 leading-5 text-amber-700 dark:text-amber-300">{tLog('desk_batch_n_pending', { n: batch.pending })}</span>}
          <span aria-hidden className="text-muted-foreground">{open ? '▾' : '▸'}</span>
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {batch.raw_text && (
            <div>
              <button type="button" className="text-xs text-muted-foreground underline" onClick={() => setShowText(s => !s)}>
                {showText ? t('desk_batch_hide_text') : t('desk_batch_show_text')}
              </button>
              {showText && <p className="mt-1 whitespace-pre-wrap rounded-lg bg-muted/50 p-2 text-xs text-foreground" data-batch-text>{batch.raw_text}</p>}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] text-muted-foreground">
                  <th className="py-1 pr-2 font-medium">{t('desk_batch_col_field')}</th>
                  <th className="py-1 pr-2 font-medium">{t('desk_batch_col_current')}</th>
                  <th className="py-1 pr-2 font-medium">{t('desk_batch_col_proposed')}</th>
                  <th className="py-1 font-medium">{t('desk_batch_col_decision')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(f => {
                  if (f.change_kind === 'unchanged' && !showUnchanged) return null
                  const decided = f.decision && f.decision !== 'pending'
                  const d = decisions[f.field]
                  return [
                    <tr key={f.field} className="border-t border-border/50 align-top" data-batch-row={f.field}>
                      <td className="py-1.5 pr-2">
                        <button type="button" className="text-left hover:underline" onClick={() => toggle(f.field)} aria-expanded={expanded.has(f.field)}>
                          {fieldLabel(f.field)}
                        </button>
                        <span className={`ml-1.5 rounded-full border px-1.5 text-[10px] leading-4 ${TONE[f.change_kind]}`}>{t('desk_batch_kind_' + f.change_kind)}</span>
                        {f.change_type === 'correction' && <span className="ml-1 text-[10px] text-muted-foreground">{t('desk_batch_correction')}</span>}
                      </td>
                      <td className="py-1.5 pr-2 text-muted-foreground">{show(f.current_value)}</td>
                      <td className="py-1.5 pr-2">{f.change_kind === 'remove' ? t('desk_batch_removed') : show(f.proposed_value)}</td>
                      <td className="py-1.5">
                        {decided ? (
                          <span className="flex flex-wrap items-center gap-1.5 text-xs">
                            <span className="rounded-full border border-border px-1.5 leading-5">{t('desk_batch_decision_' + f.decision)}</span>
                            <span className="text-muted-foreground">{f.decided_by_name || ''} · {when(f.decided_at)}</span>
                            {f.decision === 'accept' && <button type="button" disabled={busy} className="underline" onClick={() => undo([f.field])}>{t('desk_batch_undo')}</button>}
                            {['reject', 'keep', 'unresolved', 'undone'].includes(f.decision) && !['unchanged', 'ambiguous'].includes(f.change_kind) && (
                              <button type="button" disabled={busy} className="underline" onClick={() => apply([], [{ field: f.field, decision: 'accept' }])}>{t('desk_batch_reconsider')}</button>
                            )}
                          </span>
                        ) : f.change_kind === 'unchanged' ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : f.change_kind === 'add' ? (
                          <span className="flex flex-wrap gap-1"><Choice value="accept" current={d} onPick={v => pick(f.field, v)}>✓ {t('desk_batch_add')}</Choice><Choice value="reject" current={d} onPick={v => pick(f.field, v)}>{t('desk_batch_skip')}</Choice></span>
                        ) : f.change_kind === 'conflict' ? (
                          <span className="flex flex-wrap gap-1"><Choice value="keep" current={d} onPick={v => pick(f.field, v)}>{t('desk_batch_keep')}</Choice><Choice value="accept" current={d} onPick={v => pick(f.field, v)}>{t('desk_batch_use_new')}</Choice><Choice value="unresolved" current={d} onPick={v => pick(f.field, v)}>{t('desk_batch_unresolved')}</Choice></span>
                        ) : f.change_kind === 'remove' ? (
                          <span className="flex flex-wrap gap-1"><Choice value="accept" current={d} onPick={v => pick(f.field, v)}>{t('desk_batch_remove')}</Choice><Choice value="keep" current={d} onPick={v => pick(f.field, v)}>{t('desk_batch_keep')}</Choice><Choice value="unresolved" current={d} onPick={v => pick(f.field, v)}>{t('desk_batch_unresolved')}</Choice></span>
                        ) : (
                          <span className="flex flex-wrap gap-1"><Choice value="unresolved" current={d} onPick={v => pick(f.field, v)}>{t('desk_batch_unresolved')}</Choice><Choice value="reject" current={d} onPick={v => pick(f.field, v)}>{t('desk_batch_skip')}</Choice></span>
                        )}
                      </td>
                    </tr>,
                    expanded.has(f.field) && (
                      <tr key={f.field + ':why'} className="text-xs text-muted-foreground">
                        <td colSpan={4} className="pb-2">
                          {f.quote ? <p>“{f.quote}”</p> : <p>{t('desk_batch_no_quote')}</p>}
                          {f.current_source?.source_kind && (
                            <p>{tLog('desk_batch_current_source', { source: sourceLabel(f.current_source.source_kind), portal: f.current_source.portal ? ` · ${f.current_source.portal}` : '' })}</p>
                          )}
                          {(f.history || []).length > 1 && (
                            <ul className="mt-1 space-y-0.5">
                              {f.history.map(h => <li key={h.id}>{when(h.decided_at)} · {t('desk_batch_decision_' + h.decision)} · {h.decided_by_name || ''}{h.note ? ` · ${h.note}` : ''}</li>)}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ),
                  ]
                })}
              </tbody>
            </table>
          </div>
          {counts.unchanged > 0 && (
            <button type="button" className="text-xs text-muted-foreground underline" onClick={() => setShowUnchanged(s => !s)}>
              {showUnchanged ? t('desk_batch_hide_unchanged') : tLog('desk_batch_show_unchanged', { n: counts.unchanged })}
            </button>
          )}
          {stale && (
            <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-2 text-xs" data-batch-stale>
              <p className="font-medium text-amber-700 dark:text-amber-300">{t('desk_batch_stale_title')}</p>
              <ul className="mt-1">{stale.map(s => <li key={s.field}>{fieldLabel(s.field)}: {show(s.reviewed)} → {show(s.now)}</li>)}</ul>
              <button type="button" disabled={busy} className={`${pill('none', { door: true, extra: IDLE })} mt-1`} onClick={() => apply(stale.map(s => s.field))}>{t('desk_batch_apply_anyway')}</button>
            </div>
          )}
          {undoAsk && (
            <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-2 text-xs" data-batch-undo-ask>
              <p className="font-medium text-amber-700 dark:text-amber-300">{t('desk_batch_undo_changed_title')}</p>
              <ul className="mt-1 space-y-1">
                {undoAsk.items.map(it => (
                  <li key={it.field} className="flex flex-wrap items-center gap-1.5">
                    <span>{fieldLabel(it.field)}: {tLog('desk_batch_undo_changed_row', { accepted: show(it.accepted_value), now: show(it.current_value), source: sourceLabel(it.current_source) })}</span>
                    <Choice value="confirm" current={undoAsk.choice[it.field]} onPick={v => setUndoAsk(a => ({ ...a, choice: { ...a.choice, [it.field]: v } }))}>{t('desk_batch_undo_anyway')}</Choice>
                    <Choice value="skip" current={undoAsk.choice[it.field]} onPick={v => setUndoAsk(a => ({ ...a, choice: { ...a.choice, [it.field]: v } }))}>{t('desk_batch_leave')}</Choice>
                  </li>
                ))}
              </ul>
              <button type="button" disabled={busy || undoAsk.items.some(it => !undoAsk.choice[it.field])}
                className={`${pill('none', { door: true, extra: IDLE })} mt-1`}
                onClick={() => undo(undoAsk.fields, undoAsk.items.filter(it => undoAsk.choice[it.field] === 'confirm').map(it => it.field),
                  undoAsk.items.filter(it => undoAsk.choice[it.field] === 'skip').map(it => it.field))}>
                {t('desk_batch_undo_continue')}
              </button>
            </div>
          )}
          {msg && <p className="text-xs text-amber-700 dark:text-amber-300">{msg}</p>}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            {accepted.length > 0
              ? <button type="button" disabled={busy} className="text-xs text-muted-foreground underline" onClick={() => undo([])} data-batch-undo-all>{t('desk_batch_undo_all')}</button>
              : <span />}
            <button type="button" disabled={busy || !toSend.length} onClick={() => apply()} data-batch-apply
              className={pill('none', { door: true, extra: toSend.length ? ON : IDLE })}>
              {tLog('desk_batch_apply', { n: toSend.length })}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function PasteBatchHistory({ batches, sourceLabel, fieldLabel, onDecide, onUndo, busy }) {
  const list = batches || []
  if (!list.length) return null
  return (
    <div className="space-y-2" data-paste-history>
      {list.map((b, i) => (
        <PasteBatch key={b.proposal_id} batch={b} number={list.length - i} sourceLabel={sourceLabel} fieldLabel={fieldLabel}
          onDecide={onDecide} onUndo={onUndo} busy={busy} defaultOpen={i === 0 || b.pending > 0} />
      ))}
    </div>
  )
}
